import { useMemo, useEffect, useState } from 'react'

import {
  Group,
  Tooltip,
  Box,
  MultiSelect,
  SegmentedControl,
  Loader,
  Text,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  BULK_ASSIGN_TRAININGS,
  GET_COURSES,
  GET_TRAININGS,
} from 'src/graphql/trainings.graphql'
import IconChecklist from 'src/icons/IconChecklist'
import { cn } from 'src/utils'

const MAX_BULK_APPLICANTS_FOR_TRAINING = 50

type BulkResult = {
  applicantId: string
  success: boolean
  errorMsg?: string
  successMsg?: string
}

type BulkApplicantResult = BulkResult[] | null

type AssignmentType = 'lessons' | 'courses'

const BulkLessonAndCourseDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('lessons')
  const [selectedLessons, setSelectedLessons] = useState<string[]>([])
  const [selectedCourses, setSelectedCourses] = useState<string[]>([])
  const [deadline, setDeadline] = useState<Date | null>(null)

  // QUERIES:
  const { data: trainingsData, loading: trainingsLoading } =
    useQuery(GET_TRAININGS)
  const { data: coursesData, loading: coursesLoading } = useQuery(GET_COURSES)

  const availableTrainings = trainingsData?.trainings?.items || []
  const availableCourses = coursesData?.courses?.items || []

  const lessonData = useMemo(
    () =>
      availableTrainings.map((training) => ({
        label: training.name,
        value: training.trainingId,
        description: training.description,
        duration: training.duration,
        language: training.language,
      })),
    [availableTrainings]
  )

  const courseData = useMemo(
    () =>
      availableCourses.map((course) => ({
        label: course.name,
        value: course.courseId,
        description: course.description,
        numberOfTrainings: course.trainings?.length + ' lessons',
        trainings: course.trainings,
      })),
    [availableCourses]
  )

  // MUTATIONS:
  const [bulkAssignTrainings, { loading: bulkLoading }] =
    useMutation(BULK_ASSIGN_TRAININGS, {
      refetchQueries: refetchQueries,
      onCompleted: (data) => {
        const results = data?.bulkAssignTrainings?.applicants || []
        const successCount = results.filter((r) => r.success).length
        const failCount = results.filter((r) => !r.success).length

        if (setResults) {
          setResults(
            results.map((r) => ({
              applicantId: r.applicantId,
              success: r.success,
              errorMsg: r.errorMsg || 'Failed to assign training',
            }))
          )
        }

        if (failCount === 0) {
          toast('Trainings assigned successfully', 'success')
        } else if (successCount === 0) {
          toast('Failed to assign trainings', 'error')
        } else {
          toast(
            `Assigned trainings to ${successCount} applicants, ${failCount} failed`,
            'warning'
          )
        }
      },
      onError: (error) => {
        console.error('Bulk assign trainings error:', error)
        if (setResults) {
          setResults(
            applicants.map((a) => ({
              applicantId: a.applicantId,
              success: false,
              errorMsg: 'Failed to assign training',
            }))
          )
        }
        toast('Unable to assign trainings, please try again.', 'error')
      },
    })

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkLoading)
    }
  }, [bulkLoading, setLoading])

  // Reset selections when assignment type changes
  useEffect(() => {
    setSelectedLessons([])
    setSelectedCourses([])
  }, [assignmentType])

  const handleSubmit = () => {
    bulkAssignTrainings({
      variables: {
        applicantIds: applicants.map((applicant) => applicant.applicantId),
        trainingIds: assignmentType === 'lessons' ? selectedLessons : null,
        courseIds: assignmentType === 'courses' ? selectedCourses : null,
        deadline: deadline,
      },
    })
  }

  const hasSelection =
    (assignmentType === 'lessons' && selectedLessons.length > 0) ||
    (assignmentType === 'courses' && selectedCourses.length > 0)

  const isLoading = trainingsLoading || coursesLoading

  const noDataAvailable =
    !isLoading &&
    availableTrainings.length === 0 &&
    availableCourses.length === 0

  const noDataForCurrentType =
    (assignmentType === 'lessons' &&
      !trainingsLoading &&
      availableTrainings.length === 0) ||
    (assignmentType === 'courses' &&
      !coursesLoading &&
      availableCourses.length === 0)

  const canSubmit =
    hasSelection &&
    applicants.length > 0 &&
    applicants.length <= MAX_BULK_APPLICANTS_FOR_TRAINING &&
    !noDataAvailable

  const getTooltipLabel = () => {
    if (noDataAvailable) {
      return 'No lessons or courses available'
    }
    if (applicants.length > MAX_BULK_APPLICANTS_FOR_TRAINING) {
      return `Maximum ${MAX_BULK_APPLICANTS_FOR_TRAINING} applicants allowed`
    }
    if (!hasSelection) {
      return `Please select at least one ${assignmentType === 'lessons' ? 'lesson' : 'course'}`
    }
    return ''
  }

  const RenderLessonOption = ({ option }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <div className="flex flex-col gap-1 font-medium text-doubleNickel-gray-900">
        <div className="flex flex-row items-center gap-2">
          {option.label}
          {option?.language && (
            <div className="font-normal text-doubleNickel-gray-600">{`[${option.language}]`}</div>
          )}
          <div className="ml-auto font-normal">{option.duration}</div>
        </div>
        <div className="font-normal text-doubleNickel-gray-700">
          <div className="flex flex-col gap-1 font-normal text-doubleNickel-gray-700">
            <div className={cn(expanded ? 'line-clamp-none' : 'line-clamp-1')}>
              {option.description}
            </div>
            {option.description && (
              <Box
                className="text-xs font-medium text-doubleNickel-brand-400 hover:cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  setExpanded(!expanded)
                }}
              >
                {expanded ? 'Hide' : 'Show more'}
              </Box>
            )}
          </div>
        </div>
      </div>
    )
  }

  const RenderCourseOption = ({ option }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <div className="flex flex-col gap-1 font-medium text-doubleNickel-gray-900">
        <div className="flex flex-row items-center gap-2">
          {option.label}
          <div className="ml-auto font-normal text-doubleNickel-gray-600">
            {option.numberOfTrainings}
          </div>
        </div>
        <div className="font-normal text-doubleNickel-gray-700">
          <div className="flex flex-col gap-1 font-normal text-doubleNickel-gray-700">
            <div className={cn(expanded ? 'line-clamp-none' : 'line-clamp-1')}>
              {option.description}
            </div>
            {option.description && (
              <Box
                className="text-xs font-medium text-doubleNickel-brand-400 hover:cursor-pointer hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  setExpanded(!expanded)
                }}
              >
                {expanded ? 'Hide' : 'Show more'}
              </Box>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader size="md" />
          </div>
        ) : noDataAvailable ? (
          <div className="flex flex-col items-center justify-center gap-4 py-8">
            <Empty
              title="No lessons or courses available"
              subtitle="Please contact the Double Nickel team to set up your training integration."
            />
          </div>
        ) : (
          <>
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Assignment Type</Text>
              <div className="w-[250px]">
                <SegmentedControl
                  value={assignmentType}
                  onChange={(value) => setAssignmentType(value as AssignmentType)}
                  data={[
                    { label: 'Lessons', value: 'lessons' },
                    { label: 'Courses', value: 'courses' },
                  ]}
                  fullWidth
                  size="xs"
                />
              </div>
            </div>

            <div className="flex flex-row items-center justify-between">
              <Text size="xs">
                {assignmentType === 'lessons' ? 'Lessons' : 'Courses'}
              </Text>
              <div className="w-[250px]">
                {assignmentType === 'lessons' ? (
                  <MultiSelect
                    placeholder="Select lessons"
                    searchable
                    hidePickedOptions
                    data={lessonData}
                    value={selectedLessons}
                    maxDropdownHeight={300}
                    onChange={(value) => setSelectedLessons(value)}
                    renderOption={RenderLessonOption}
                    disabled={bulkLoading}
                    size="xs"
                    classNames={{
                      input: 'text-xs',
                      dropdown: 'border-doubleNickel-gray-200',
                      option:
                        'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                    }}
                  />
                ) : (
                  <MultiSelect
                    placeholder="Select courses"
                    searchable
                    hidePickedOptions
                    data={courseData}
                    value={selectedCourses}
                    maxDropdownHeight={300}
                    onChange={(value) => setSelectedCourses(value)}
                    renderOption={RenderCourseOption}
                    disabled={bulkLoading}
                    size="xs"
                    classNames={{
                      input: 'text-xs',
                      dropdown: 'border-doubleNickel-gray-200',
                      option:
                        'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                    }}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Deadline (optional)</Text>
              <div className="w-[250px]">
                <DateInput
                  placeholder="Select date"
                  valueFormat="MM/DD/YYYY"
                  minDate={new Date()}
                  maxDate={new Date(new Date().setMonth(new Date().getMonth() + 3))}
                  value={deadline}
                  onChange={(date) => setDeadline(date)}
                  clearable
                  disabled={bulkLoading}
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right',
                  }}
                />
              </div>
            </div>

            {noDataForCurrentType && (
              <div className="flex flex-col items-center justify-center gap-4">
                <Empty
                  title={`No ${assignmentType} available`}
                  subtitle="Please contact the Double Nickel team to set up your training integration."
                />
              </div>
            )}
          </>
        )}
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkLoading}
        />
        <Tooltip disabled={canSubmit} label={getTooltipLabel()}>
          <Button
            text={'Assign'}
            variant="filled"
            lefticon={<IconChecklist />}
            disabled={!canSubmit}
            loading={bulkLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkLessonAndCourseDrawer
